import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizablePaneOptions {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

interface UseResizablePaneReturn {
  width: number;
  handleMouseDown: (e: React.MouseEvent) => void;
}

export function useResizablePane({
  defaultWidth,
  minWidth,
  maxWidth,
}: UseResizablePaneOptions): UseResizablePaneReturn {
  const [width, setWidth] = useState(defaultWidth);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(
        maxWidth,
        Math.max(minWidth, startWidth.current + delta)
      );
      setWidth(newWidth);
    };

    const handleMouseUp = (): void => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return (): void => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [maxWidth, minWidth]);

  return { width, handleMouseDown };
}
